import { initiateAudio } from './audioFunctions'
import {
    canvasTouchEnd,
    canvasTouchMove,
    canvasTouchStart,
    canvasTouchCancel,
    canvasTouchDoubleTap,
    canvasMouseDown,
    canvasMouseMove,
    canvasMouseUp,
    canvasMouseDblClick,
} from './canvasMouseMethods'

import { paint, schedulePaint } from './paint'
import { CurrentAppState } from '../Models/AppState'
import { deal, setButtonImages, shuffle, loadCardBackImage, startNewGame, normaliseSeed } from './setupCards'
import {
    cardState,
    getDraggedCards,
    setCardHeight,
    setCardSpacing,
    setCardWidth,
    setCardYOffset,
    setFoundationXOffset,
    setWindowHeight,
    setWindowWidth,
    undo,
    appState,
    getCurrentCardState,
    setCardState,
    restoreCardState,
    checkForWin,
    setAppState,
    clearUndoHistory,
} from './state'
import { assignCardCoordinates } from './assignCardCoordinates'
import { saveGameState, flushGameState } from './stateManager'
import {
    menuButtonX,
    menuButtonY,
    undoButtonX,
    undoButtonY,
    newGameButtonX,
    newGameButtonY,
    autoCompleteButtonX,
    autoCompleteButtonY,
} from './allXYCoordinates'
import { saveSettings } from './settingsManager'
import { testScenarios } from './testScenarios'
import { executeMenuAction, setFavicon } from './menuActions'
import { getMenuHitAreas } from './drawMenu'
import { runTestActions } from './testRunner'
import { themes } from './themes'
import { disableCardIntegrity } from './cardIntegrity'
import { defaultSettings } from '../Models/Settings'
import { initGameCenter } from './gameCenterManager'
import { fetchTipProducts } from './tipJarManager'
import { findOverlapTableau, isCardOverTableau } from './findCardIndex'
import { getCardPosition, clearAllAnimationPositions, clearAllDragPositions } from './cardPositions'
import {
    timerRestore,
    timerResumeRestored,
    timerReset,
    timerGetElapsed,
    formatTime,
    timerPause,
    timerResume,
    timerIsStarted,
} from './timer'
import { loadStatistics } from './statisticsManager'
import { getDrawRecordsDebugCounter, resetDrawRecordsDebugCounter } from './drawRecords'
import { getRenderDebugSnapshot, resetRenderDebugFrameCounter } from './renderDebug'

// ── Splash screen theme colouring ──────────────────────────────────
// Theme colours for the splash are now applied by an inline <script> in
// index.html so they take effect before any external resource loads.
// The applySplashTheme() IIFE that was here has been moved there.

// Cached safe area insets — read once to avoid DOM thrashing and resize loops
let _cachedSafeAreaInsets: { top: number; bottom: number; left: number; right: number } | null = null

/**
 * Resolve requested in-app test scenario key.
 * Supports either URL param (?test=draw1) or native injection from iOS simulator
 * launch env (window.__nativeTestScenario).
 */
function getRequestedTestScenarioKey(): string | null {
    const urlParams = new URLSearchParams(window.location.search)
    const testParam = urlParams.get('test')
    if (testParam) return testParam

    const nativeScenario = (window as any).__nativeTestScenario
    if (typeof nativeScenario === 'string' && nativeScenario.trim().length > 0) {
        return nativeScenario.trim()
    }

    return null
}

// Read CSS env(safe-area-inset-*) values via a temporary DOM element.
// Results are cached; call invalidateSafeAreaCache() on orientation change to re-read.
function getSafeAreaInsets(): { top: number; bottom: number; left: number; right: number } {
    if (_cachedSafeAreaInsets) return _cachedSafeAreaInsets

    // Prefer native safe-area values injected by ViewController.swift (Cordova).
    // CSS env(safe-area-inset-*) returns 0 in Cordova's WKWebView, so the native
    // side bridges the real device insets via window.__nativeSafeArea.
    const native = (window as any).__nativeSafeArea
    if (native && (native.top > 0 || native.bottom > 0)) {
        _cachedSafeAreaInsets = {
            top: native.top,
            bottom: native.bottom,
            left: native.left ?? 0,
            right: native.right ?? 0,
        }
        return _cachedSafeAreaInsets
    }

    // Fallback: read CSS env() values (works in PWA / standalone mode).
    const div = document.createElement('div')
    div.style.cssText =
        'position:fixed;top:0;left:0;right:0;visibility:hidden;pointer-events:none;' +
        'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);' +
        'padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px);'
    document.documentElement.appendChild(div)
    const cs = getComputedStyle(div)
    const result = {
        top: parseFloat(cs.paddingTop) || 0,
        bottom: parseFloat(cs.paddingBottom) || 0,
        left: parseFloat(cs.paddingLeft) || 0,
        right: parseFloat(cs.paddingRight) || 0,
    }
    div.remove()
    _cachedSafeAreaInsets = result
    return result
}

function invalidateSafeAreaCache(): void {
    _cachedSafeAreaInsets = null
}

// Helper function to calculate card dimensions and spacing
function calculateCardDimensions(windowHeight: number, windowWidth: number) {
    const isPortrait = windowHeight > windowWidth

    // Read actual device safe area insets (Dynamic Island, home indicator, etc.)
    // In Cordova, these come from native Swift via window.__nativeSafeArea.
    // In PWA/standalone mode, they come from CSS env(safe-area-inset-*).
    // In Safari browser, the browser chrome already insets the viewport, so 0 is correct.
    const safeArea = getSafeAreaInsets()

    // Top padding: use safe area inset if present, otherwise a minimal aesthetic buffer.
    const topPadding = safeArea.top > 0 ? safeArea.top + 5 : 10

    // Bottom padding: similarly, safe area > 0 means we need to avoid the home indicator.
    const bottomPadding = safeArea.bottom > 0 ? safeArea.bottom + 5 : 10

    if (isPortrait) {
        const usableHeight = windowHeight - topPadding - bottomPadding

        // Uniform 9-row grid: all rows (buttons and cards) are the same height.
        // 9 rows of cardHeight + 8 gaps of cardSpacing = usableHeight
        const cardHeight = Math.floor(usableHeight / 9.5)
        const cardWidth = Math.floor(cardHeight * 0.75)
        const cardSpacing = Math.max(2, Math.floor((usableHeight - 9 * cardHeight) / 8))

        // cardYOffset is the top margin — first row starts here
        const cardXOffset = Math.max(topPadding, Math.floor(cardSpacing * 0.5))
        const foundationXOffset = cardHeight * 2 + cardSpacing * 3

        return {
            cardHeight,
            cardWidth,
            cardSpacing,
            cardXOffset,
            foundationXOffset,
        }
    } else {
        // Landscape/Desktop: optimize based on available space
        const availableHeight = windowHeight - topPadding - bottomPadding
        const availableWidth = windowWidth * 0.95

        // Calculate card size based on height constraint
        const maxCardHeightFromScreen = Math.floor(availableHeight / 8.56)
        const maxCardWidthFromScreen = Math.floor(availableWidth / 8)
        const maxCardHeightFromWidth = Math.floor(maxCardWidthFromScreen / 0.75)

        // Use the smaller of the two to ensure everything fits
        const cardHeight = Math.min(maxCardHeightFromScreen, maxCardHeightFromWidth)
        const cardWidth = Math.floor(cardHeight * 0.75)
        const totalGapSpace = availableHeight - 8 * cardHeight
        const cardSpacing = Math.max(2, Math.floor(totalGapSpace / 7))
        const cardXOffset = Math.max(topPadding, Math.floor(cardSpacing * 3.75))
        const foundationXOffset = cardHeight * 2 + cardSpacing * 3

        return {
            cardHeight,
            cardWidth,
            cardSpacing,
            cardXOffset,
            foundationXOffset,
        }
    }
}

// Load cordova.js dynamically in Cordova environments (file is injected by Cordova build, not present during web dev)
;(function loadCordova() {
    const script = document.createElement('script')
    script.src = 'cordova.js'
    script.onerror = () => {
        // Expected to fail in browser dev mode — cordova.js only exists in Cordova builds
    }
    document.head.appendChild(script)

    // Dismiss the native Cordova splash screen once cordova.js is ready.
    // AutoHideSplashScreen=false keeps the native splash visible during WKWebView
    // cold-start. We dismiss it here (after deviceready) so our HTML splash is
    // already painted underneath — seamless transition, no blank gap.
    document.addEventListener('deviceready', () => {
        // Double-rAF ensures the browser has composited a frame with the HTML splash
        // before we pull the native splash away.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                // cordova-ios 8 built-in CDVLaunchScreen plugin
                if ((window as any).cordova?.exec) {
                    ;(window as any).cordova.exec(null, null, 'LaunchScreen', 'hide', [])
                }
                // Legacy cordova-plugin-splashscreen
                else if ((navigator as any).splashscreen) {
                    ;(navigator as any).splashscreen.hide()
                }
            })
        })
    })
})()

// Callback for native safe-area insets injected by ViewController.swift (Cordova).
// CSS env(safe-area-inset-*) returns 0 in Cordova's WKWebView, so the native side
// bridges the real device insets and calls this function when they're available.
;(window as any).__onNativeSafeArea = () => {
    invalidateSafeAreaCache()

    // If pageSetup hasn't run yet the new values will be picked up naturally.
    // If layout has already been calculated, recalculate now.
    if (appState.windowHeight > 0) {
        const dimensions = calculateCardDimensions(appState.windowHeight, appState.windowWidth)
        appState.cardWidth = dimensions.cardWidth
        appState.cardHeight = dimensions.cardHeight
        appState.cardSpacing = dimensions.cardSpacing
        appState.cardYOffset = dimensions.cardXOffset
        appState.foundationXOffset = dimensions.foundationXOffset

        for (let i = 0; i < cardState.length; i++) {
            cardState[i] = assignCardCoordinates(cardState[i])
        }
        saveGameState(cardState)
        schedulePaint()
    }
}

window.addEventListener('load', async () => {
    // Record the moment the HTML splash is actually on-screen.
    // In Cordova the native splash may run for seconds before the webview
    // renders, so performance.now() at module-eval time is unreliable.
    _splashVisibleAt = performance.now()

    // ── Debug & test globals ──────────────────────────────────────────
    // Exposed on window so e2e tests (Playwright) and the in-app test runner
    // can inspect / manipulate game state programmatically.
    // These carry no security risk in a single-player offline card game.
    //@ts-ignore
    window.draw = paint
    //@ts-ignore
    window.paint = paint
    //@ts-ignore
    window.cardsState = cardState
    //@ts-ignore
    window.cardState = cardState
    //@ts-ignore
    window.appState = appState
    //@ts-ignore
    window.getDraggedCards = getDraggedCards
    //@ts-ignore
    window.getCurrentCardState = getCurrentCardState
    //@ts-ignore
    window.setCardState = setCardState
    //@ts-ignore — e2e tests: seed partial states, disabling integrity checks for the session
    window.seedCardState = (state: any) => {
        disableCardIntegrity()
        setCardState(state, { skipIntegrity: true })
    }
    //@ts-ignore — e2e tests: manually disable/enable integrity if needed
    window.disableCardIntegrity = disableCardIntegrity
    //@ts-ignore
    window.assignCardCoordinates = assignCardCoordinates
    //@ts-ignore
    window.menuButtonX = menuButtonX
    //@ts-ignore
    window.menuButtonY = menuButtonY
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.undoButtonX = undoButtonX
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.undoButtonY = undoButtonY
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.newGameButtonX = newGameButtonX
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.newGameButtonY = newGameButtonY
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.autoCompleteButtonX = autoCompleteButtonX
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.autoCompleteButtonY = autoCompleteButtonY
    //@ts-ignore — e2e tests use this to execute menu actions without tapping canvas
    window.executeMenuAction = executeMenuAction
    //@ts-ignore — e2e tests: inspect current canvas menu hit areas
    window.getMenuHitAreas = getMenuHitAreas
    //@ts-ignore — e2e tests: overlap detection debugging
    window.findOverlapTableau = findOverlapTableau
    //@ts-ignore — e2e tests: point-based tableau detection
    window.isCardOverTableau = isCardOverTableau
    //@ts-ignore — e2e tests: get card rendered position (including drag overlay)
    window.getCardPosition = getCardPosition
    //@ts-ignore — e2e tests: programmatic double-tap (bypasses pickup/undo race)
    window.canvasTouchDoubleTap = canvasTouchDoubleTap
    //@ts-ignore — e2e tests: clear animation overlays before programmatic interactions
    window.clearAllAnimationPositions = clearAllAnimationPositions
    //@ts-ignore — e2e tests: clear drag overlays before programmatic interactions
    window.clearAllDragPositions = clearAllDragPositions
    //@ts-ignore — dev: get current timer elapsed time in ms
    window.timerGetElapsed = timerGetElapsed
    //@ts-ignore — dev: format timer time
    window.formatTime = formatTime
    //@ts-ignore — dev: load game statistics
    window.loadStatistics = loadStatistics
    //@ts-ignore — e2e debug: number of leaderboard draw calls
    window.getDrawRecordsDebugCounter = getDrawRecordsDebugCounter
    //@ts-ignore — e2e debug: reset leaderboard draw counter
    window.resetDrawRecordsDebugCounter = resetDrawRecordsDebugCounter
    //@ts-ignore — e2e debug: latest render telemetry snapshot
    window.getRenderDebugSnapshot = getRenderDebugSnapshot
    //@ts-ignore — e2e debug: reset render telemetry frame counter
    window.resetRenderDebugFrameCounter = resetRenderDebugFrameCounter
    //@ts-ignore
    window.undo = () => {
        undo()
        paint()
    }
    //@ts-ignore — dev command: start a new game with a deterministic seed
    window.newGameWithSeed = (seed: string | number) => {
        startNewGame(normaliseSeed(seed))
    }
    //@ts-ignore — dev command: run a test scenario by name ('draw1' or 'draw3')
    window.runTest = async (name: string) => {
        const scenario = testScenarios[name]
        if (!scenario) {
            console.error(`Unknown test scenario: ${name}. Available: ${Object.keys(testScenarios).join(', ')}`)
            return
        }
        // Set draw count and start seeded game, then run the test
        appState.NUM_CARDS_FLIPPED = scenario.drawCount
        appState.settings.drawCount = scenario.drawCount
        await startNewGame(scenario.seed)
        // Wait for paint
        await new Promise((r) => setTimeout(r, 500))
        return runTestActions(scenario.actions, scenario.name)
    }

    await pageSetup()
})

// Debounced resize handler to recalculate layout and repaint
let resizeTimeout: number | null = null
window.addEventListener('resize', () => {
    // Clear previous timeout
    if (resizeTimeout) {
        clearTimeout(resizeTimeout)
    }

    // Set new timeout to trigger layout recalculation after resize stops
    resizeTimeout = window.setTimeout(() => {
        // Update window dimensions
        const windowHeight = window.innerHeight
        const windowWidth = window.innerWidth

        // Skip recalculation if dimensions haven't actually changed
        // (prevents unnecessary reflows from spurious resize events on iOS)
        if (windowHeight === appState.windowHeight && windowWidth === appState.windowWidth) {
            resizeTimeout = null
            return
        }

        appState.windowWidth = windowWidth
        appState.windowHeight = windowHeight

        // Orientation may have changed — invalidate cached safe area insets
        invalidateSafeAreaCache()

        // Recalculate card dimensions using shared function
        const dimensions = calculateCardDimensions(windowHeight, windowWidth)

        appState.cardWidth = dimensions.cardWidth
        appState.cardHeight = dimensions.cardHeight
        appState.cardSpacing = dimensions.cardSpacing
        appState.cardYOffset = dimensions.cardXOffset
        appState.foundationXOffset = dimensions.foundationXOffset

        // Reassign coordinates for ALL states in history (not just current)
        // so undo doesn't reveal states with stale positions from old dimensions
        for (let i = 0; i < cardState.length; i++) {
            const state = cardState[i]
            cardState[i] = assignCardCoordinates(state)
        }
        // Persist updated positions without adding a new history entry
        saveGameState(cardState)

        // Repaint with new layout
        schedulePaint()

        resizeTimeout = null
    }, 300) // 300ms debounce delay
})

window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyS') {
        console.log({ cardsState: cardState })
    }
})

window.addEventListener(
    'touchstart',
    (e) => {
        canvasTouchStart(e)
    },
    { passive: false }
)
window.addEventListener(
    'touchend',
    (e) => {
        canvasTouchEnd(e)
    },
    { passive: false }
)
window.addEventListener(
    'touchmove',
    (e) => {
        canvasTouchMove(e)
    },
    { passive: false }
)
window.addEventListener(
    'touchcancel',
    () => {
        canvasTouchCancel()
    },
    { passive: false }
)
window.addEventListener(
    'gestureend',
    (e) => {
        e.preventDefault()
    },
    { passive: false }
)

window.addEventListener(
    'mousedown',
    (e) => {
        canvasMouseDown(e)
    },
    { passive: false }
)
window.addEventListener(
    'mousemove',
    (e) => {
        canvasMouseMove(e)
    },
    { passive: false }
)
window.addEventListener(
    'mouseup',
    (e) => {
        canvasMouseUp(e)
    },
    { passive: false }
)
window.addEventListener(
    'dblclick',
    (e) => {
        canvasMouseDblClick(e)
    },
    { passive: false }
)

// Ensure settings are saved before page unload
window.addEventListener('beforeunload', () => {
    flushGameState()
    saveSettings(appState.settings)
    // Pause timer to persist accumulated time (includes current session)
    if (appState.currentAppState === CurrentAppState.Playing) {
        timerPause()
    }
})

// Save settings and pause timer when tab becomes hidden (mobile/desktop tab switching)
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
        flushGameState()
        saveSettings(appState.settings)
        if (appState.currentAppState === CurrentAppState.Playing && timerIsStarted()) {
            timerPause()
        }
    } else if (document.visibilityState === 'visible') {
        // Resume timer when returning to the tab (only if game is actively playing)
        if (appState.currentAppState === CurrentAppState.Playing && timerIsStarted()) {
            timerResume()
        }
    }
})

// Repaint when fullscreen state changes so the button icon updates
document.addEventListener('fullscreenchange', () => {
    schedulePaint()
})

async function pageSetup() {
    try {
        // Calculate the card info
        const windowHeight = window.innerHeight
        const windowWidth = window.innerWidth

        const dimensions = calculateCardDimensions(windowHeight, windowWidth)

        initiateAudio()
        initGameCenter()
        fetchTipProducts()

        setButtonImages()
        await loadCardBackImage()

        setWindowHeight(windowHeight)
        setWindowWidth(windowWidth)
        setCardHeight(dimensions.cardHeight)
        setCardWidth(dimensions.cardWidth)
        setCardSpacing(dimensions.cardSpacing)
        setCardYOffset(dimensions.cardXOffset)
        setFoundationXOffset(dimensions.foundationXOffset)

        // Detect standalone/PWA/Cordova mode — hides fullscreen button
        appState.isStandalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            // @ts-ignore — iOS Safari standalone property
            window.navigator.standalone === true ||
            document.referrer.includes('android-app://') ||
            // @ts-ignore — Cordova global
            window.cordova !== undefined

        // Check for automated test scenario request from URL (?test=...)
        // or native simulator launch env (injected into window.__nativeTestScenario)
        const requestedScenarioKey = getRequestedTestScenarioKey()
        const urlParams = new URLSearchParams(window.location.search)
        const testScenario = requestedScenarioKey ? testScenarios[requestedScenarioKey] : null

        // Reset settings to known defaults for test runs so tests don't depend
        // on localStorage state left behind by previous test runs
        if (testScenario) {
            appState.settings = { ...defaultSettings }
            // Reset theme to match default settings
            appState.currentTheme =
                themes[defaultSettings.themeName as keyof typeof themes] ?? themes.HappyPurpleTheme
        }

        // If the test scenario specifies a handedness, apply it before the URL param
        // override so ?hand= can still take precedence for debugging
        if (testScenario?.handedness) {
            appState.settings.handedness = testScenario.handedness
        }

        // A ?hand=left or ?hand=right URL param overrides handedness (dev/testing)
        const handParam = urlParams.get('hand')
        if (handParam === 'left' || handParam === 'right') {
            appState.settings.handedness = handParam
        }

        // A ?seed= URL param forces a fresh deterministic deal (dev/testing),
        // bypassing any saved state from localStorage.
        const urlSeed = urlParams.get('seed')

        // If running a test scenario, override the seed and draw count
        const effectiveSeed = testScenario ? testScenario.seed : urlSeed != null ? normaliseSeed(urlSeed) : null

        if (testScenario) {
            // Configure draw count for the test scenario
            appState.NUM_CARDS_FLIPPED = testScenario.drawCount
            appState.settings.drawCount = testScenario.drawCount
        }

        const stateRestored = effectiveSeed == null ? restoreCardState() : false

        if (effectiveSeed != null) {
            // Seeded game (or test): ignore saved state, deal a deterministic hand
            timerReset()
            const shuffledDeck = shuffle(effectiveSeed, false)
            deal(shuffledDeck)
        } else if (stateRestored) {
            // State was restored from localStorage
            // Restore the timer so elapsed time persists across page refreshes
            timerRestore()

            // Reassign coordinates for ALL states in history, not just the current one.
            // Older states may have positions from a different screen size / orientation.
            // If undo() reveals one of those states, stale positions would cause cards to jump.
            for (let i = 0; i < cardState.length; i++) {
                const state = cardState[i]
                const updatedState = assignCardCoordinates(state)
                cardState[i] = updatedState
            }
            // Persist the updated positions
            saveGameState(cardState)

            // Check if the restored game was already won
            if (checkForWin()) {
                clearUndoHistory()
                const newAppState = { ...appState }
                newAppState.currentAppState = CurrentAppState.Completed
                setAppState(newAppState)
            } else {
                // Game is still in progress — resume the timer
                timerResumeRestored()
            }
        } else {
            // No saved state, start a new game with random shuffle
            timerReset()
            const shuffledDeck = shuffle()
            deal(shuffledDeck)
        }

        document?.documentElement?.style?.setProperty(
            '--currentBackgroundColor',
            appState.currentTheme.backgroundColor
        )

        document?.documentElement?.style?.setProperty(
            '--currentHighlightColor',
            appState.currentTheme.highlightColor
        )

        // Set favicon to match the active theme
        setFavicon(appState.settings.themeName)

        // A ?menu=1 URL param opens the menu immediately (for screenshots/dev)
        if (urlParams.get('menu') === '1') {
            const newAppState = { ...appState }
            newAppState.currentAppState = CurrentAppState.Menu
            setAppState(newAppState)
        }

        // A ?theme=ThemeName URL param overrides the active theme (dev/testing)
        const themeParam = urlParams.get('theme')
        if (themeParam && themeParam in themes) {
            const theme = themes[themeParam as keyof typeof themes]
            appState.currentTheme = theme
            appState.settings.themeName = themeParam
            document?.documentElement?.style?.setProperty('--currentBackgroundColor', theme.backgroundColor)
            document?.documentElement?.style?.setProperty('--currentHighlightColor', theme.highlightColor)
            setButtonImages()
            loadCardBackImage()
            setFavicon(themeParam)
        }

        schedulePaint()
    } catch (error) {
        console.error('pageSetup() failed:', error)
    } finally {
        // Always hide loading screen, even if setup fails —
        // a broken game is better than an eternal loading screen
        hideLoadingScreen()

        // If a test scenario was requested, start the test runner after a short delay
        // to allow the first paint to render
        const scenarioKey = getRequestedTestScenarioKey()
        const scenario = scenarioKey ? testScenarios[scenarioKey] : null
        if (scenario) {
            setTimeout(async () => {
                const result = await runTestActions(scenario.actions, scenario.name)
                // Log final result prominently
                if (result.passed) {
                    console.log(
                        `%c TEST PASSED: ${scenario.name} — ${result.completedActions} actions in ${(result.duration / 1000).toFixed(1)}s`,
                        'color: #00cc00; font-size: 16px; font-weight: bold'
                    )
                } else {
                    console.error(
                        `%c TEST FAILED: ${scenario.name} at action ${result.failedAction?.index}: ${result.failedAction?.error}`,
                        'color: #ff0000; font-size: 16px; font-weight: bold'
                    )
                }
            }, 500)
        }
    }
}

// Minimum time (ms) the HTML splash must be visible *after* the window 'load'
// event fires.  In Cordova the native splash screen may display for several
// seconds before the webview starts, so performance.now() at module-evaluation
// time can already exceed any absolute threshold.  Instead we record a
// timestamp when 'load' fires (= when our HTML splash is first painted) and
// measure relative to that.
let _splashVisibleAt = 0
const MINIMUM_SPLASH_MS = 2000

function hideLoadingScreen() {
    const loadingScreen = document.getElementById('loading-screen')
    if (!loadingScreen) return

    // ?splash=hold keeps it visible indefinitely (dev/screenshot tool)
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('splash') === 'hold') return

    // Skip minimum display time for automated tests — no need to wait 2s
    const isTestRun = getRequestedTestScenarioKey() != null
    const elapsed = performance.now() - _splashVisibleAt
    const remaining = isTestRun ? 0 : Math.max(0, MINIMUM_SPLASH_MS - elapsed)

    setTimeout(() => {
        loadingScreen.classList.add('fade-out')
        // Remove from DOM after fade-out animation completes
        setTimeout(() => {
            loadingScreen.remove()
        }, 600)
    }, remaining)
}
